/**
 * ShiftModal - Dialog to confirm a shift assignment after a drop.
 *
 * Lets the user pick a full (8h) or partial shift with custom times. Owns its
 * own local form state (times, shift type); the target employee/date/worksite
 * come in as props and the result is reported via onConfirm.
 */
import { useState } from 'react';
import type { WorksiteForPlanning } from './types';

interface ShiftModalProps {
  isOpen: boolean;
  employeeName: string;
  date: string;
  worksite: WorksiteForPlanning | null;
  onConfirm: (startTime: string, endTime: string) => void;
  onCancel: () => void;
}

export function ShiftModal({ isOpen, employeeName, date, worksite, onConfirm, onCancel }: ShiftModalProps) {
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('16:00');
  const [shiftType, setShiftType] = useState<'full' | 'partial'>('full');

  if (!isOpen || !worksite) return null;

  // Las horas que el backend derivará de los horarios (regla espejo de
  // `PlannedShiftCreate._horas_previstas`): si la salida no pasa la entrada,
  // el turno cruza la medianoche y suma 24 h. Sin esto el modal prometía
  // horarios y debajo se guardaban 8 h fijas (ronda 3, r2b21[2]).
  const horasPrevistas = (() => {
    const aMinutos = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    let minutos = aMinutos(endTime) - aMinutos(startTime);
    if (minutos <= 0) minutos += 24 * 60;
    return minutos / 60;
  })();

  const handleConfirm = () => {
    if (shiftType === 'full') {
      onConfirm('08:00', '16:00');
    } else {
      onConfirm(startTime, endTime);
    }
  };

  const dateFormatted = new Date(date).toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-xl">
        <div className="p-4 border-b border-line bg-surface-2">
          <h2 className="text-lg font-semibold">Asignar turno</h2>
        </div>

        <div className="p-4 space-y-4">
          {/* Info */}
          <div className="p-3 bg-info-soft rounded-lg">
            <p className="font-medium text-info">{employeeName}</p>
            <p className="text-sm text-info capitalize">{dateFormatted}</p>
            <p className="text-sm text-brand mt-1">
              Proyecto: {worksite.code} - {worksite.name}
            </p>
          </div>

          {/* Shift type */}
          <div>
            <label className="block text-sm font-medium text-ink-2 mb-2">
              Tipo de turno
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setShiftType('full')}
                className={`
                  flex-1 py-2 rounded-lg border-2 font-medium transition-colors
                  ${shiftType === 'full'
                    ? 'border-brand bg-info-soft text-info'
                    : 'border-line text-ink-2 hover:bg-surface-2'
                  }
                `}
              >
                Jornada completa (8h)
              </button>
              <button
                onClick={() => setShiftType('partial')}
                className={`
                  flex-1 py-2 rounded-lg border-2 font-medium transition-colors
                  ${shiftType === 'partial'
                    ? 'border-brand bg-info-soft text-info'
                    : 'border-line text-ink-2 hover:bg-surface-2'
                  }
                `}
              >
                Parcial
              </button>
            </div>
          </div>

          {/* Custom times */}
          {shiftType === 'partial' && (
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm text-ink-2 mb-1">Entrada</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full px-3 py-2 border border-line-strong rounded-lg"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm text-ink-2 mb-1">Salida</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full px-3 py-2 border border-line-strong rounded-lg"
                />
              </div>
            </div>
          )}

          {shiftType === 'partial' && (
            <p className="text-sm text-ink-2" data-testid="horas-previstas">
              Se guardarán {horasPrevistas.toLocaleString('es-ES')} h previstas
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onCancel}
              className="flex-1 py-2 text-ink-2 rounded-lg hover:bg-surface-2 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 py-2 bg-brand text-white rounded-lg font-medium hover:bg-brand-2 transition-colors"
            >
              Asignar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
